<div class="applicant-profile-container">

  <p class="section-description">Personal information and additional data</p>

  <!-- ═══════════════════ BASIC INFO SECTION ═══════════════════ -->
  <div class="profile-section" [class.collapsed]="collapsedSections['basicInfo']">

    <!-- Section Header -->
    <div class="profile-section-header">
      <div class="section-title">
        <mat-icon svgIcon="icon-user-gradient"></mat-icon>
        <span class="title-text">Basic Info</span>
      </div>

      <div class="header-actions">
        <!-- Verified extraction info badge -->
        <app-banner variant="neutral" message="Fields with a green tick were updated from verified extraction data.">
        </app-banner>

        <button class="collapse-btn" type="button" (click)="toggleSection('basicInfo')">
          <mat-icon svgIcon="minus"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Section Body -->
    @if (!collapsedSections['basicInfo']) {
    <div class="profile-section-body">
      <div class="personal-info-grid three-col">
        @for (field of getBasicInfoFields(); track field.label) {
        <button type="button" class="input-field-wrapper" [class.copyable-field]="canCopyValues"
          [disabled]="!canCopyValues" (click)="onCopyValue(field.value)">
          <mat-icon class="field-icon" [svgIcon]="field.icon"></mat-icon>
          <div class="input-container">
            <span class="field-label">{{ field.label }}</span>
            <span class="field-value">
              {{ field.value }}
              @if (canCopyValues) {
              <mat-icon class="copy-trigger" svgIcon="icon-copy-card"></mat-icon>
              }
              @if (field.verified) {
              <mat-icon class="verified-tick" svgIcon="icon-check"></mat-icon>
              }
            </span>
          </div>
        </button>
        }
      </div>
    </div>
    }
  </div>


  <!-- ═══════════════════ ADDITIONAL INFO SECTION ═══════════════════ -->
  <div class="profile-section" [class.collapsed]="collapsedSections['additionalInfo']">

    <!-- Section Header -->
    <div class="profile-section-header">
      <div class="section-title">
        <mat-icon svgIcon="icon-user-gradient"></mat-icon>
        <span class="title-text">Additional Info</span>
      </div>

      <div class="header-actions">
        <app-banner variant="neutral" message="Common Across Applicants"> </app-banner>

        @if (!isReadOnly() && !additionalInfoEditMode()) {
        <app-buttons class="secondary-btn" label="EDIT" variant="secondary" (clicked)="onEditClick()">
          <mat-icon btn-icon-start svgIcon="edit"></mat-icon>
        </app-buttons>
        } @else if (!isReadOnly()) {
        <app-buttons (clicked)="onSave()" [disabled]="isSaveDisabled" label="Save">
        </app-buttons>
        <app-buttons variant="secondary" class="secondary-btn" (clicked)="onCancelEdit()" label="Cancel">
        </app-buttons>
        }

        <button class="collapse-btn" type="button" (click)="toggleSection('additionalInfo')">
          <mat-icon svgIcon="minus"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Section Body -->
    @if (!collapsedSections['additionalInfo']) {
    <div class="profile-section-body">

      <!-- ── Sponsorship Questions ── -->
      <h4 class="sub-section-title">Sponsorship Questions</h4>

      @if (!additionalInfoEditMode()) {
      <!-- VIEW MODE -->
      <div class="personal-info-grid three-col">
        @for (question of getVisibleSponsorshipQuestions(); track question.label) {
        <button type="button" class="input-field-wrapper" [class.copyable-field]="canCopyValues"
          [disabled]="!canCopyValues" (click)="onCopyValue(question.value)">
          <mat-icon class="field-icon" [svgIcon]="question.icon"></mat-icon>
          <div class="input-container">
            <span class="field-label">{{ question.label }}</span>
            <span class="field-value">
              {{ question.value }}
              @if (canCopyValues) {
              <mat-icon class="copy-trigger" svgIcon="icon-copy-card"></mat-icon>
              }
            </span>
          </div>
        </button>
        }
      </div>
      } @else {
      <!-- EDIT MODE -->
      <form [formGroup]="additionalInfoForm">
        <div class="personal-info-grid three-col">

          @if (isDependentApplicant()) {
          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="icon-user-outline"></mat-icon>
            <div class="input-container">
              <app-dropdown formControlName="relationshipToSelf" [internalLabel]="'Relationship to Self'"
                [options]="getRelationshipOptions()" [searchFlag]="false" [multiselect]="false" [required]="true"
                [class.edit-dropdown-mode]="additionalInfoEditMode()">
              </app-dropdown>
            </div>
          </div>
          }

          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="icon-user-outline"></mat-icon>
            <div class="input-container">
              <app-dropdown formControlName="sponsor" [internalLabel]="'Who is the Sponsor'"
                [options]="getWhoIsSponsorOptions()" [searchFlag]="false" [multiselect]="false" [required]="true"
                [class.edit-dropdown-mode]="additionalInfoEditMode()">
              </app-dropdown>
            </div>
          </div>
          @if(!isSponsorSelf()){

          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="icon-user-outline"></mat-icon>
            <div class="input-container">
              <app-input class="sponsor-name-input" [formGroup]="additionalInfoForm" formControlName="sponsorName"
                controlName="sponsorName" [isEdit]="true" [isClearIcon]="!isSponsorNameReadOnly"
                [showLabelInView]="true" [required]="true" [disabled]="isSponsorNameReadOnly"
                [label]="'Sponsor Name / Employer Name'">
              </app-input>
            </div>
          </div>
          
          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="sponsor_type_icon"></mat-icon>
            <div class="input-container">
              <app-dropdown formControlName="sponsorType" [internalLabel]="'Sponsor Type'"
                [options]="getSponsorTypeOptions()" [searchFlag]="false" [multiselect]="false" [required]="true"
                [class.edit-dropdown-mode]="additionalInfoEditMode()">
              </app-dropdown>
            </div>
          </div>


          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="globe_icon"></mat-icon>
            <div class="input-container">
              <app-dropdown formControlName="sponsorEmirate" internalLabel="Sponsor's Emirates"
                [options]="getSponsorEmirateOptions()" [searchFlag]="false" [multiselect]="false" [required]="true"
                [class.edit-dropdown-mode]="additionalInfoEditMode()">
              </app-dropdown>
            </div>
          </div>
          }
          <div class="input-field-wrapper">
            <mat-icon class="field-icon" svgIcon="quote-personal-budget"></mat-icon>
            <div class="input-container">
              <app-dropdown formControlName="sponsorSalaryBand" [internalLabel]="'Sponsor Salary Band'"
                [options]="getSalaryBandOptions()" [searchFlag]="false" [multiselect]="false" [required]="true"
                [class.edit-dropdown-mode]="additionalInfoEditMode()">
              </app-dropdown>
            </div>
          </div>

        </div>
      </form>
      }

      <!-- ── Policy Details ── -->
      <h4 class="sub-section-title">Policy Details</h4>

      @if (!additionalInfoEditMode()) {
      <!-- VIEW MODE -->
      <div class="personal-info-grid three-col">
        @for (detail of getPolicyDetails(); track detail.label) {
        <button type="button" class="input-field-wrapper" [class.copyable-field]="canCopyValues"
          [disabled]="!canCopyValues" (click)="onCopyValue(detail.value)">
          <mat-icon class="field-icon" [svgIcon]="detail.icon"></mat-icon>
          <div class="input-container">
            <span class="field-label">{{ detail.label }}</span>
            <span class="field-value">
              {{ detail.value }}
              @if (canCopyValues) {
              <mat-icon class="copy-trigger" svgIcon="icon-copy-card"></mat-icon>
              }
            </span>
          </div>
        </button>
        }
      </div>
      } @else {
      <!-- EDIT MODE -->
      <form [formGroup]="additionalInfoForm">
        <div class="personal-info-grid three-col">
          <div class="input-field-wrapper">
            <app-datepicker [isEdit]="true" [isClearIcon]="true" [datepickerIcon]="'prefix'"
              [formGroup]="additionalInfoForm" formControlName="policyStartDate" [required]="true"
              [minDate]="minPolicyStartDate" [maxDate]="maxPolicyStartDate" [label]="'Policy Start Date'">
            </app-datepicker>
          </div>
        </div>
      </form>
      }

    </div>
    }
  </div>

</div>